import { ReactNode, createContext, useContext, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { Button } from '@givebutter/primitives/button';
import { Checkbox } from '@givebutter/primitives/checkbox';
import { Label } from '@givebutter/primitives/label';
import { faEnvelope, faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { faCircleNotch, faPaperPlane as faPaperPlaneSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Dialog from 'components/dialog';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import { upperFirst } from 'lodash';

interface IResendItemDialogContext {
    itemType: 'ticket' | 'receipt';
    email: string | undefined;
    forceRender: Function;
    resendEmail: Function;
    isLoading: boolean;
    isDisabled: boolean;
}

const ResendItemDialogContext = createContext<IResendItemDialogContext>({
    itemType: 'ticket',
    email: '',
    forceRender: () => {},
    resendEmail: () => {},
    isLoading: false,
    isDisabled: false,
});

const ResendItemDialogFormBody = () => {
    const { itemType, email, forceRender, resendEmail, isLoading } =
        useContext(ResendItemDialogContext);
    const [sendToDifferentEmail, setSendToDifferentEmail] = useState(!email);

    const { handleSubmit, path, control } = Form.useGivebutterFormContext();

    const submit = (values: FieldValues) => {
        resendEmail(sendToDifferentEmail ? values.email : email);
    };

    return (
        <Dialog.Body>
            <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-4">
                {email ? (
                    <p className={'text-base'}>
                        The email that we have for this {itemType} is{' '}
                        <span className="font-medium">{email}</span>. To send it to another email
                        address, select send to another person below.
                    </p>
                ) : (
                    <p>
                        There is no email associated with this {itemType}. Please enter the email
                        you would like it sent to below.
                    </p>
                )}
                <div>
                    <div className={!sendToDifferentEmail ? '[&>div]:opacity-60' : ''}>
                        <Form.Container>
                            <Form.Label className="sr-only" name={path('email')}>
                                Name
                            </Form.Label>
                            <Form.TextInputRoot
                                name={path('email')}
                                control={control}
                                rules={{
                                    required: sendToDifferentEmail && 'Email is required',
                                }}
                                inputRootProps={{
                                    disabled: !sendToDifferentEmail,
                                }}
                            >
                                <FontAwesomeIcon icon={faEnvelope} />
                                <Form.TextInputForm placeholder={email} type="email" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('email')} />
                        </Form.Container>
                    </div>
                </div>
                {email ? (
                    <Label className="flex w-fit cursor-pointer items-center gap-x-2">
                        <Checkbox
                            checked={sendToDifferentEmail}
                            onCheckedChange={checked => {
                                if (typeof checked === 'boolean') {
                                    setSendToDifferentEmail(checked);
                                }
                            }}
                        />
                        Send to another person
                    </Label>
                ) : null}
                <div className="flex items-center justify-center gap-4 px-2">
                    <Button
                        variant="outline"
                        color="neutral"
                        onClick={() => forceRender?.()}
                        className="w-1/2"
                    >
                        Cancel
                    </Button>
                    <Form.UseFormSubmitButton className="w-1/2" disabled={isLoading}>
                        {isLoading ? (
                            <FontAwesomeIcon
                                className={'flex h-5 w-5 animate-spin'}
                                icon={faCircleNotch}
                            />
                        ) : (
                            <p>Resend {upperFirst(itemType)}</p>
                        )}
                    </Form.UseFormSubmitButton>
                </div>
            </form>
        </Dialog.Body>
    );
};

const ResendItemDialogDropdownItem = () => {
    const { itemType, isDisabled } = useContext(ResendItemDialogContext);

    return (
        <Dialog.Trigger asChild={true}>
            <DropdownMenu.Item
                disabled={isDisabled}
                onSelect={e => e.preventDefault()}
                className="flex items-center gap-2"
            >
                <FontAwesomeIcon size={'sm'} icon={faPaperPlane} />
                <p>Resend {itemType}</p>
            </DropdownMenu.Item>
        </Dialog.Trigger>
    );
};

const ResendItemDialogIcon = () => {
    return (
        <div className="relative flex aspect-square h-8 items-center justify-center rounded-full text-white after:absolute after:-inset-1 after:-z-[1] after:rounded-full after:bg-gray-100 after:p-2">
            <FontAwesomeIcon className={'text-gray-500'} icon={faPaperPlaneSolid} size={'lg'} />
        </div>
    );
};

const ResendItemFormHeader = () => {
    const { itemType } = useContext(ResendItemDialogContext);

    return (
        <Dialog.Header>
            <Dialog.Title className="!text-xl font-medium">{`Where should we send the ${itemType}?`}</Dialog.Title>
        </Dialog.Header>
    );
};

interface ResendItemDialogContentProps {
    children: ReactNode;
}

const ResendItemDialogContent = ({ children }: ResendItemDialogContentProps) => (
    <Dialog.Content className="max-w-[400px]">{children}</Dialog.Content>
);

const Header = ResendItemFormHeader;
const FormBody = ResendItemDialogFormBody;
const DropdownItem = ResendItemDialogDropdownItem;
const Icon = ResendItemDialogIcon;
const Content = ResendItemDialogContent;

const Portal = Dialog.Portal;
const Overlay = Dialog.Overlay;

export {
    ResendItemDialogContext,
    //
    Header,
    FormBody,
    DropdownItem,
    Icon,
    Content,
    //
    Portal,
    Overlay,
    //
    ResendItemFormHeader,
    ResendItemDialogFormBody,
    ResendItemDialogDropdownItem,
    ResendItemDialogIcon,
    ResendItemDialogContent,
};
